import { NoteAttachments } from "../notes/NoteAttachments";
import { SegmentedControl } from "@/components/ui/segmented-control";
import { useIsMobile } from "@/hooks/use-mobile";
import { HelpHint } from "../misc/HelpHint";
import { ActivityAuthor } from "../activity/ActivityAuthor";
import { useState } from "react";
import { AddProjectExpense } from "./AddProjectExpense";
import {
  BUDGET_ZONES,
  type BudgetZone,
  adjustedBalances,
} from "./adjustmentModel";
import { useGetList, useDataProvider } from "ra-core";
import { ReferenceField } from "@/components/admin/reference-field";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import type { CrmDataProvider } from "../providers/types";
import type { Deal, Expense } from "../types";
import {
  FINANCIAL_ZONES,
  type FinancePlan,
  type ZoneKey,
} from "./financeModel";
import {
  EXPENSE_REASONS,
  expenseCategoryLabel,
  rubles,
  expenseBalances,
  type ExpenseReview,
  type ExpenseTotal,
  type ExpenseReason,
} from "./expenseModel";
export function ProjectExpenses({
  project,
  plan,
}: {
  project: Deal;
  plan?: FinancePlan;
}) {
  const isMobile = useIsMobile();
  const [scope, setScope] = useState("project"),
    [page, setPage] = useState(1);
  const provider = useDataProvider<CrmDataProvider>();
  const ledger = useQuery({
    queryKey: ["financial_analytics", "project-ledger", project.id],
    queryFn: () =>
      provider.getFinancialAnalytics({
        start: null,
        end: null,
        deal_id: project.id,
      }),
    select: (data) => data.adjustments,
  });
  const totals = useGetList<ExpenseTotal>("project_expense_totals", {
    filter: { deal_id: project.id },
    pagination: { page: 1, perPage: 4 },
    sort: { field: "zone_key", order: "ASC" },
  });
  const pending = useGetList<Expense>("expenses", {
    filter:
      scope === "project"
        ? { deal_id: project.id, status: "pending" }
        : { "deal_id@is": null, status: "pending" },
    pagination: { page, perPage: 10 },
    sort: { field: "created_at", order: "ASC" },
  });
  const history = useGetList<ExpenseReview>("expense_reviews", {
    filter: { deal_id: project.id },
    pagination: { page: 1, perPage: 20 },
    sort: { field: "reviewed_at", order: "DESC" },
  });
  const balances =
    plan &&
    !totals.error &&
    !totals.isPending &&
    !ledger.error &&
    !ledger.isPending
      ? expenseBalances(plan, totals.data ?? [], ledger.data ?? [])
      : undefined;
  return (
    <section className="rounded-2xl border bg-card p-4 sm:p-5 space-y-5">
      <div className="flex items-center gap-1">
        <h3 className="font-semibold">Расходы проекта</h3>
        <HelpHint>
          Плановый расход списывается из выбранной зоны. Неплановый расход
          переносит сумму из бюджета ответственного в выбранный бюджет, без
          повторного списания.
        </HelpHint>
      </div>
      <details className="rounded-xl border p-3">
        <summary className="cursor-pointer text-sm font-medium">
          Остатки бюджетов и списания
        </summary>
        <div className="mt-3 space-y-3">
          {totals.error || ledger.error ? (
            <p role="alert">Не удалось загрузить подтверждённые суммы</p>
          ) : totals.isPending || ledger.isPending ? (
            <p>Загрузка расходов…</p>
          ) : balances ? (
            <>
              <p className="text-sm">
                Грязная прибыль проекта после корректировок:{" "}
                <strong>
                  {rubles(
                    adjustedBalances(
                      plan!,
                      totals.data ?? [],
                      ledger.data ?? [],
                    ).find((r) => r.key === "reserve")?.balance ?? 0,
                  )}
                </strong>
              </p>
              <div className="grid sm:grid-cols-2 gap-3 rounded-lg bg-muted p-3">
                <p>
                  Списано из бюджетов зон:{" "}
                  <strong>{rubles(balances.spent)}</strong>
                </p>
                <p>
                  Цена КП минус списания из бюджетов зон:{" "}
                  <strong>{rubles(balances.remainder)}</strong>
                </p>
              </div>
              <HelpHint>
                Остаток не означает полученные деньги или чистую прибыль. Заявки
                на проверке и отклонённые суммы в расходах не учитываются.
              </HelpHint>
              {isMobile ? (
                <div className="space-y-3">
                  {balances.rows.map((row) => (
                    <div key={row.key} className="rounded-xl border p-3">
                      <div className="flex justify-between items-center gap-2">
                        <h4 className="text-sm font-medium">
                          {
                            FINANCIAL_ZONES.find((z) => z.key === row.key)
                              ?.label
                          }
                        </h4>
                        <strong
                          className={
                            row.balance < 0
                              ? "text-destructive text-sm"
                              : "text-primary text-sm"
                          }
                        >
                          {rubles(row.balance)}
                        </strong>
                      </div>
                      <dl className="grid grid-cols-2 gap-3 mt-3 text-xs">
                        <div>
                          <dt className="text-muted-foreground">Бюджет</dt>
                          <dd className="mt-1 font-medium">
                            {rubles(row.budget)}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-muted-foreground">Списано</dt>
                          <dd className="mt-1 font-medium">
                            {rubles(row.spent)}
                          </dd>
                        </div>
                      </dl>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="py-2">Зона</th>
                        <th>Бюджет после корректировок</th>
                        <th>Списания</th>
                        <th>Остаток</th>
                      </tr>
                    </thead>
                    <tbody>
                      {balances.rows.map((row) => (
                        <tr key={row.key} className="border-b">
                          <td className="py-2">
                            {
                              FINANCIAL_ZONES.find((z) => z.key === row.key)
                                ?.label
                            }
                          </td>
                          <td>{rubles(row.budget)}</td>
                          <td>{rubles(row.spent)}</td>
                          <td
                            className={
                              row.balance < 0
                                ? "text-destructive font-medium"
                                : ""
                            }
                          >
                            {rubles(row.balance)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm">
              Сохраните финансовый план перед подтверждением расходов.
            </p>
          )}
        </div>
      </details>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h4 className="font-medium">Заявки на проверку</h4>
        <AddProjectExpense project={project} plan={plan} />
      </div>
      <SegmentedControl
        label="Область заявок"
        value={scope}
        onValueChange={(value) => {
          setScope(value);
          setPage(1);
        }}
        options={[
          { value: "project", label: "Заявки проекта" },
          { value: "unassigned", label: "Без проекта" },
        ]}
      />
      {scope === "unassigned" && (
        <HelpHint>
          Старые заявки без проекта. Решение явно привяжет выбранную заявку к «
          {project.name}».
        </HelpHint>
      )}
      {pending.isPending ? (
        <p>Загрузка заявок…</p>
      ) : pending.error ? (
        <p role="alert">Не удалось загрузить заявки</p>
      ) : pending.data?.length ? (
        pending.data.map((expense) => (
          <ReviewExpense
            key={String(expense.id) + ":" + expense.revision}
            expense={expense}
            project={project}
            plan={plan}
          />
        ))
      ) : (
        <p className="text-sm text-muted-foreground">Заявок на проверке нет.</p>
      )}
      {((pending.total ?? 0) > 10 || page > 1) && (
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Назад
          </Button>
          <span>Страница {page}</span>
          <Button
            variant="outline"
            disabled={page * 10 >= (pending.total ?? 0)}
            onClick={() => setPage((p) => p + 1)}
          >
            Далее
          </Button>
        </div>
      )}
      <h4 className="font-medium">Последние решения</h4>
      {history.error ? (
        <p role="alert">Не удалось загрузить историю</p>
      ) : (
        history.data?.map((r) => (
          <div key={r.id} className="border rounded-md p-3 text-sm">
            <p>
              Заявка #{r.expense_id} ·{" "}
              {r.decision === "approved" ? "Подтверждена" : "Отклонена"} ·{" "}
              {rubles(r.amount)}
            </p>
            <p>
              {r.zone_key
                ? FINANCIAL_ZONES.find((z) => z.key === r.zone_key)?.label
                : "Без списания по зоне"}{" "}
              · {EXPENSE_REASONS.find((x) => x.value === r.reason)?.label}
            </p>
            {r.budget_adjustment_id != null && (
              <p>
                Перенос в{" "}
                {
                  BUDGET_ZONES.find(
                    (z) => z.key === (r.budget_to_zone ?? "reserve"),
                  )?.label
                }{" "}
                · корректировка #{r.budget_adjustment_id}. Повторное списание не
                выполняется.
              </p>
            )}
            <p className="whitespace-pre-line">{r.note}</p>
            {r.expense_snapshot && (
              <NoteAttachments note={r.expense_snapshot} />
            )}
            {r.expense_snapshot?.beneficiary_sales_id != null && (
              <p>
                Сотрудник:{" "}
                <ReferenceField
                  record={{ ...r.expense_snapshot, id: r.expense_id }}
                  source="beneficiary_sales_id"
                  reference="sales"
                  link={false}
                  render={({ referenceRecord }) =>
                    referenceRecord
                      ? `${referenceRecord.first_name} ${referenceRecord.last_name}`
                      : "Не указан"
                  }
                />
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              <ActivityAuthor id={r.reviewed_by_sales_id} /> ·{" "}
              {new Date(r.reviewed_at).toLocaleString("ru-RU", {
                timeZone: "Europe/Moscow",
              })}
            </p>
          </div>
        ))
      )}
    </section>
  );
}
export function ReviewExpense({
  expense,
  project,
  plan,
}: {
  expense: Expense;
  project: Deal;
  plan?: FinancePlan;
}) {
  const [zone, setZone] = useState<ZoneKey>(
      expense.proposed_zone ?? "administration",
    ),
    [reason, setReason] = useState<ExpenseReason>(
      expense.expense_reason ?? "planned",
    ),
    [note, setNote] = useState("");
  const [to, setTo] = useState<BudgetZone>(
    expense.proposed_to_zone ?? "reserve",
  );
  const [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const provider = useDataProvider<CrmDataProvider>(),
    cache = useQueryClient();
  const decide = async (decision: "approved" | "rejected") => {
    if (busy || !note.trim()) return;
    setBusy(true);
    setError("");
    try {
      await provider.reviewProjectExpense({
        expense_id: expense.id,
        deal_id: project.id,
        expected_revision: expense.revision,
        decision,
        zone_key: zone,
        reason,
        note,
        to_zone: reason === "planned" ? null : to,
      });
      await Promise.all(
        [
          "expenses",
          "expense_reviews",
          "project_expense_totals",
          "financial_analytics",
          "project_budget_adjustments",
        ].map((resource) => cache.invalidateQueries({ queryKey: [resource] })),
      );
    } catch (e) {
      setError(
        (e as { message?: string })?.message ?? "Не удалось сохранить решение",
      );
    } finally {
      setBusy(false);
    }
  };
  const spent = useGetList<ExpenseTotal>("project_expense_totals", {
    filter: { deal_id: project.id },
    pagination: { page: 1, perPage: 4 },
    sort: { field: "zone_key", order: "ASC" },
  });
  const ledger = useQuery({
    queryKey: ["financial_analytics", "project-ledger", project.id],
    queryFn: () =>
      provider.getFinancialAnalytics({
        start: null,
        end: null,
        deal_id: project.id,
      }),
    select: (data) => data.adjustments,
  });
  const budget = plan
    ? (expenseBalances(plan, [], ledger.data ?? []).rows.find(
        (r) => r.key === zone,
      )?.budget ?? 0)
    : 0;
  const used = Number(
    spent.data?.find((t) => t.zone_key === zone)?.approved_amount ?? 0,
  );
  return (
    <fieldset
      disabled={busy || !!project.archived_at}
      className="border rounded-lg p-3 space-y-3"
    >
      <legend className="font-medium text-sm">
        Заявка #{expense.id} · {expenseCategoryLabel(expense.category)} ·{" "}
        {rubles(expense.amount)}
      </legend>
      <p className="text-sm whitespace-pre-line">
        {expense.comment || "Пояснение не указано"}
      </p>
      <NoteAttachments note={expense} />
      <p className="text-xs text-muted-foreground">
        Дата: {expense.spent_at} · Автор:{" "}
        <ActivityAuthor id={expense.created_by_sales_id} /> · Задача{" "}
        {expense.task_id ? "#" + expense.task_id : "не указана"}
      </p>
      <label className="block text-sm">
        {reason === "planned"
          ? "Бюджет для списания"
          : "Бюджет ответственного для компенсации"}
        <select
          className="w-full h-9 border rounded-md px-2 bg-background"
          value={zone}
          onChange={(e) => {
            const next = e.target.value as ZoneKey;
            setZone(next);
            if (to === next) setTo("reserve");
          }}
        >
          {FINANCIAL_ZONES.map((z) => (
            <option key={z.key} value={z.key}>
              {z.label}
            </option>
          ))}
        </select>
      </label>
      {reason === "planned" &&
        plan &&
        !spent.isPending &&
        !spent.error &&
        !ledger.isPending &&
        !ledger.error &&
        used + Number(expense.amount) > budget && (
          <p className="text-sm text-destructive">
            После подтверждения перерасход зоны составит{" "}
            {rubles(used + Number(expense.amount) - budget)}. Решение сохранится
            с этим отклонением.
          </p>
        )}
      <label className="block text-sm">
        Причина для финансового учёта
        <select
          className="w-full h-9 border rounded-md px-2 bg-background"
          value={reason}
          onChange={(e) => setReason(e.target.value as ExpenseReason)}
        >
          {EXPENSE_REASONS.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
      </label>
      {reason !== "planned" && (
        <label className="block text-sm">
          Куда добавить
          <select
            className="w-full h-9 border rounded-md px-2 bg-background"
            value={to}
            onChange={(e) => setTo(e.target.value as BudgetZone)}
          >
            {BUDGET_ZONES.filter((z) => z.key !== zone).map((z) => (
              <option key={z.key} value={z.key}>
                {z.label}
              </option>
            ))}
          </select>
        </label>
      )}
      {reason !== "planned" && (
        <p className="text-sm">
          {rubles(expense.amount)}:{" "}
          {FINANCIAL_ZONES.find((z) => z.key === zone)?.label} →{" "}
          {BUDGET_ZONES.find((z) => z.key === to)?.label}. Ответственный берётся
          из финансового плана выбранной зоны.
        </p>
      )}
      <label className="block text-sm">
        Комментарий к решению
        <Textarea
          maxLength={2000}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </label>
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <Button
          disabled={
            busy ||
            !note.trim() ||
            !plan ||
            !!project.archived_at ||
            (reason !== "planned" && to === zone)
          }
          onClick={() => decide("approved")}
        >
          {expense.deal_id == null
            ? "Привязать к этому проекту и подтвердить"
            : "Подтвердить расход"}
        </Button>
        <Button
          variant="outline"
          disabled={busy || !note.trim() || !!project.archived_at}
          onClick={() => decide("rejected")}
        >
          {expense.deal_id == null
            ? "Привязать к этому проекту и отклонить"
            : "Отклонить"}
        </Button>
      </div>
    </fieldset>
  );
}
